import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Trophy, PoundSterling, Building2, Users, Home, Car, 
  Search, TrendingUp, Info, ExternalLink, AlertTriangle,
  ChevronRight, Eye, Flag, Calendar, Clock
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// 2025-26 Budget Limits from IPSA
const BUDGETS_2025_26 = {
  london: {
    staffing: 281980,
    office: 39560,
    accommodation: 31840,
  },
  nonLondon: {
    staffing: 263370,
    office: 35930,
    accommodation: 21680,
  },
  uncapped: {
    travel: 'Uncapped',
    winding_up: 66370,
  },
  other: {
    london_living_payment: 4845,
    hotel_london: 230, // per night
    hotel_outside: 150, // per night
  }
};

// Anomaly detection - flags unusual expense patterns
const detectAnomalies = (mp, averageSpend) => {
  const anomalies = [];
  
  if (mp.total_spend > averageSpend * 1.5) {
    anomalies.push({
      type: 'high_total',
      severity: 'warning',
      message: `Total expenses ${Math.round((mp.total_spend / averageSpend - 1) * 100)}% above average`
    });
  }
  
  if (mp.total_spend > averageSpend * 2) {
    anomalies.push({
      type: 'very_high_total',
      severity: 'alert',
      message: `Total expenses over 2x the average MP`
    });
  }
  
  if (mp.travel_spend > 30000) {
    anomalies.push({
      type: 'high_travel',
      severity: 'warning',
      message: `Unusually high travel costs: ${formatCurrency(mp.travel_spend)}`
    });
  }
  
  if (mp.accommodation_spend > 30000) {
    anomalies.push({
      type: 'high_accommodation',
      severity: 'info',
      message: `High accommodation costs: ${formatCurrency(mp.accommodation_spend)}`
    });
  }
  
  if (mp.staffing_spend > 250000) {
    anomalies.push({
      type: 'high_staffing',
      severity: 'warning',
      message: `High staffing costs: ${formatCurrency(mp.staffing_spend)}`
    });
  }
  
  if (mp.office_spend > 35000) {
    anomalies.push({
      type: 'high_office',
      severity: 'info',
      message: `Above-average office costs: ${formatCurrency(mp.office_spend)}`
    });
  }
  
  return anomalies;
};

const AnomalyBadge = ({ anomalies }) => {
  if (!anomalies || anomalies.length === 0) return null;
  
  const highestSeverity = anomalies.find(a => a.severity === 'alert') 
    ? 'alert' 
    : anomalies.find(a => a.severity === 'warning') 
      ? 'warning' 
      : 'info';
  
  const bgColor = highestSeverity === 'alert' 
    ? 'bg-red-500' 
    : highestSeverity === 'warning' 
      ? 'bg-amber-500' 
      : 'bg-blue-500';
  
  return (
    <div className={`${bgColor} text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1`}>
      <AlertTriangle className="h-3 w-3" />
      <span>{anomalies.length} flag{anomalies.length > 1 ? 's' : ''}</span>
    </div>
  );
};

const LeaderboardCard = ({ mp, rank, averageSpend }) => {
  const slug = mp.name?.toLowerCase().replace(/ /g, '-').replace(/'/g, '') || '';
  const anomalies = detectAnomalies(mp, averageSpend);
  
  const getRankStyle = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900';
    if (rank === 2) return 'bg-gradient-to-r from-slate-300 to-slate-400 text-slate-800';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600 to-amber-700 text-white';
    return 'bg-slate-100 text-slate-600';
  };

  return (
    <Link 
      to={`/politician/${slug}`}
      className="block bg-white rounded-xl border border-slate-200 p-3 sm:p-4 hover:shadow-lg hover:border-emerald-300 transition-all group"
      data-testid={`mp-expense-card-${rank}`}
    >
      {/* Mobile Layout */}
      <div className="sm:hidden">
        <div className="flex items-center gap-3 mb-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${getRankStyle(rank)}`}>
            {rank}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-slate-900 text-sm truncate">{mp.name}</h3>
              <AnomalyBadge anomalies={anomalies} />
            </div>
            <p className="text-xs text-slate-500 truncate">{mp.constituency || 'Former MP'}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-base font-black text-slate-900">{formatCurrency(mp.total_spend)}</div>
            <div className="text-[10px] text-slate-500">2024-25</div>
          </div>
        </div>
        
        {/* Mobile expense breakdown - 2x2 grid */}
        <div className="grid grid-cols-2 gap-1.5 text-[11px] bg-slate-50 rounded-lg p-2">
          <div className="flex items-center gap-1 text-slate-600">
            <Building2 className="h-3 w-3 text-blue-500 flex-shrink-0" />
            <span className="truncate">Office: {formatCurrency(mp.office_spend)}</span>
          </div>
          <div className="flex items-center gap-1 text-slate-600">
            <Users className="h-3 w-3 text-green-500 flex-shrink-0" />
            <span className="truncate">Staff: {formatCurrency(mp.staffing_spend)}</span>
          </div>
          <div className="flex items-center gap-1 text-slate-600">
            <Home className="h-3 w-3 text-purple-500 flex-shrink-0" />
            <span className="truncate">Accom: {formatCurrency(mp.accommodation_spend)}</span>
          </div>
          <div className="flex items-center gap-1 text-slate-600">
            <Car className="h-3 w-3 text-orange-500 flex-shrink-0" />
            <span className="truncate">Travel: {formatCurrency(mp.travel_spend)}</span>
          </div>
        </div>
        
        {anomalies.length > 0 && (
          <div className="mt-2 text-xs text-amber-600 flex items-center gap-1">
            <Flag className="h-3 w-3" />
            <span className="truncate">{anomalies[0].message}</span>
          </div>
        )}
      </div>
      
      {/* Desktop Layout */}
      <div className="hidden sm:flex items-start gap-4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0 ${getRankStyle(rank)}`}>
          {rank}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-slate-900 truncate group-hover:text-emerald-600 transition-colors">{mp.name}</h3>
            <AnomalyBadge anomalies={anomalies} />
          </div>
          <p className="text-sm text-slate-500 truncate">{mp.constituency || 'Former MP'}</p>
          
          <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
            <div className="flex items-center gap-1 text-slate-600">
              <Building2 className="h-3 w-3 text-blue-500" />
              <span>{formatCurrency(mp.office_spend)}</span>
            </div>
            <div className="flex items-center gap-1 text-slate-600">
              <Users className="h-3 w-3 text-green-500" />
              <span>{formatCurrency(mp.staffing_spend)}</span>
            </div>
            <div className="flex items-center gap-1 text-slate-600">
              <Home className="h-3 w-3 text-purple-500" />
              <span>{formatCurrency(mp.accommodation_spend)}</span>
            </div>
            <div className="flex items-center gap-1 text-slate-600">
              <Car className="h-3 w-3 text-orange-500" />
              <span>{formatCurrency(mp.travel_spend)}</span>
            </div>
          </div>
          
          {anomalies.length > 0 && (
            <div className="mt-2 space-y-1">
              {anomalies.slice(0, 2).map((anomaly, idx) => (
                <div 
                  key={idx} 
                  className={`text-xs flex items-center gap-1 ${
                    anomaly.severity === 'alert' ? 'text-red-600' :
                    anomaly.severity === 'warning' ? 'text-amber-600' :
                    'text-blue-600'
                  }`}
                >
                  <Flag className="h-3 w-3" />
                  <span>{anomaly.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="text-right flex-shrink-0">
          <div className="text-2xl font-black text-slate-900">{formatCurrency(mp.total_spend)}</div>
          <div className="text-xs text-slate-500 mb-2">Total 2024-25</div>
          <div className="text-xs text-emerald-600 font-medium flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            View Details <ChevronRight className="h-3 w-3" />
          </div>
        </div>
      </div>
    </Link>
  );
};

// Year Tab Component
const YearTab = ({ year, isActive, onClick, hasData }) => (
  <button
    onClick={onClick}
    className={`px-3 sm:px-6 py-2 sm:py-3 font-bold rounded-t-xl transition-all text-sm sm:text-base ${
      isActive 
        ? 'bg-white text-emerald-600 border-t-2 border-x-2 border-emerald-500' 
        : hasData 
          ? 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
          : 'bg-white/5 text-white/40 hover:bg-white/10'
    }`}
  >
    <div className="flex items-center gap-1 sm:gap-2">
      <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
      <span>{year}</span>
      {!hasData && <span className="text-[10px] sm:text-xs bg-amber-500 text-white px-1.5 sm:px-2 py-0.5 rounded-full">Soon</span>}
    </div>
  </button>
);

// 2025-26 Budget Display Component
const BudgetDisplay2526 = () => (
  <div className="bg-white rounded-xl border border-slate-200 p-6">
    <div className="flex items-center gap-3 mb-6">
      <Clock className="h-6 w-6 text-amber-500" />
      <div>
        <h3 className="font-bold text-xl text-slate-900">2025-26 Data Coming April 2026</h3>
        <p className="text-sm text-slate-500">IPSA publishes actual expense claims after each financial year ends</p>
      </div>
    </div>
    
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
      <div className="flex items-start gap-3">
        <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <strong>Why isn't 2025-26 data available?</strong>
          <p className="mt-1">The 2025-26 financial year runs from 1 April 2025 to 31 March 2026. 
          IPSA publishes complete expense data approximately 2-3 months after each financial year ends. 
          Expect 2025-26 data in June/July 2026.</p>
        </div>
      </div>
    </div>
    
    <h4 className="font-bold text-lg text-slate-900 mb-4">2025-26 Budget Limits</h4>
    <p className="text-sm text-slate-500 mb-4">These are the maximum amounts MPs can claim this year:</p>
    
    <div className="grid md:grid-cols-2 gap-6">
      {/* London MPs */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
        <h5 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          London Area MPs
        </h5>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-600">Staffing Budget</span>
            <span className="font-bold text-slate-900">{formatCurrency(BUDGETS_2025_26.london.staffing)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-600">Office Costs</span>
            <span className="font-bold text-slate-900">{formatCurrency(BUDGETS_2025_26.london.office)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-600">Accommodation</span>
            <span className="font-bold text-slate-900">{formatCurrency(BUDGETS_2025_26.london.accommodation)}</span>
          </div>
          <div className="flex justify-between border-t pt-2 mt-2">
            <span className="text-slate-600">London Living Payment</span>
            <span className="font-bold text-slate-900">{formatCurrency(BUDGETS_2025_26.other.london_living_payment)}</span>
          </div>
        </div>
      </div>
      
      {/* Non-London MPs */}
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200">
        <h5 className="font-bold text-emerald-800 mb-3 flex items-center gap-2">
          <Home className="h-4 w-4" />
          Non-London Area MPs
        </h5>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-600">Staffing Budget</span>
            <span className="font-bold text-slate-900">{formatCurrency(BUDGETS_2025_26.nonLondon.staffing)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-600">Office Costs</span>
            <span className="font-bold text-slate-900">{formatCurrency(BUDGETS_2025_26.nonLondon.office)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-600">Accommodation</span>
            <span className="font-bold text-slate-900">{formatCurrency(BUDGETS_2025_26.nonLondon.accommodation)}</span>
          </div>
          <div className="flex justify-between border-t pt-2 mt-2">
            <span className="text-slate-600">Winding Up Budget</span>
            <span className="font-bold text-slate-900">{formatCurrency(BUDGETS_2025_26.uncapped.winding_up)}</span>
          </div>
        </div>
      </div>
    </div>
    
    {/* Other allowances */}
    <div className="mt-6 bg-slate-50 rounded-xl p-4 border border-slate-200">
      <h5 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
        <Car className="h-4 w-4" />
        Other Allowances
      </h5>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
        <div>
          <span className="text-slate-500">Travel & Subsistence</span>
          <p className="font-bold text-slate-900">Uncapped</p>
        </div>
        <div>
          <span className="text-slate-500">Hotel (London)</span>
          <p className="font-bold text-slate-900">£{BUDGETS_2025_26.other.hotel_london}/night</p>
        </div>
        <div>
          <span className="text-slate-500">Hotel (Outside)</span>
          <p className="font-bold text-slate-900">£{BUDGETS_2025_26.other.hotel_outside}/night</p>
        </div>
      </div>
    </div>
    
    <div className="mt-6 text-center">
      <a 
        href="https://www.theipsa.org.uk/mp-staffing-business-costs/annual-publications" 
        target="_blank" 
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium"
      >
        View IPSA Official Publications <ExternalLink className="h-4 w-4" />
      </a>
    </div>
  </div>
);

const MPExpensesPage = () => {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('total');
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyFlagged, setShowOnlyFlagged] = useState(false);
  const [selectedYear, setSelectedYear] = useState('2024-25');

  useEffect(() => {
    if (selectedYear === '2024-25') {
      fetchLeaderboard();
    }
  }, [sortBy, selectedYear]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/mp-expenses/leaderboard`, {
        params: { limit: 650, sort_by: sortBy, order: 'desc', current_only: true }
      });
      setLeaderboard(response.data.leaderboard);
      setStats({
        total_mps: response.data.total_mps,
        total_spend: response.data.total_spend_all_mps,
        average_spend: response.data.average_spend,
        year: response.data.year,
      });
    } catch (error) {
      toast.error('Failed to load expenses data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const averageSpend = stats?.average_spend || 225000;

  const filteredLeaderboard = leaderboard.filter(mp => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase().trim();
    const nameLower = mp.name.toLowerCase();
    const constituencyLower = (mp.constituency || '').toLowerCase();
    
    const queryWords = query.split(' ').filter(w => w.length > 0);
    
    return queryWords.every(word => 
      nameLower.includes(word) || constituencyLower.includes(word)
    );
  });

  const displayLeaderboard = showOnlyFlagged 
    ? filteredLeaderboard.filter(mp => detectAnomalies(mp, averageSpend).length > 0)
    : filteredLeaderboard;

  const top10 = leaderboard.slice(0, 10);
  
  const remainingMPs = searchQuery || showOnlyFlagged 
    ? displayLeaderboard 
    : displayLeaderboard.slice(10);

  const flaggedCount = leaderboard.filter(mp => detectAnomalies(mp, averageSpend).length > 0).length;

  const sortOptions = [
    { value: 'total', label: 'Total Spend', icon: <PoundSterling className="h-4 w-4" /> },
    { value: 'staffing', label: 'Staffing', icon: <Users className="h-4 w-4" /> },
    { value: 'office', label: 'Office', icon: <Building2 className="h-4 w-4" /> },
    { value: 'accommodation', label: 'Accommodation', icon: <Home className="h-4 w-4" /> },
    { value: 'travel', label: 'Travel', icon: <Car className="h-4 w-4" /> },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <Trophy className="h-6 w-6 sm:h-8 sm:w-8 text-amber-300" />
            <span className="px-2 sm:px-3 py-1 bg-white/20 rounded-full text-xs sm:text-sm font-medium text-white">
              Parliamentary Expenses
            </span>
          </div>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-black text-white mb-2 sm:mb-4">
            MP Expenses Leaderboard
          </h1>
          <p className="text-sm sm:text-lg text-white/80 max-w-2xl">
            See how your MP spends taxpayer money. Click any MP to view their full expense details.
          </p>
          
          {/* Stats Cards */}
          {stats && selectedYear === '2024-25' && (
            <div className="grid grid-cols-2 gap-2 sm:gap-4 mt-6 sm:mt-8">
              <div className="bg-white/10 backdrop-blur rounded-xl p-3 sm:p-4 text-center">
                <div className="text-2xl sm:text-3xl font-black text-white">650</div>
                <div className="text-xs sm:text-sm text-white/70">Current MPs</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-3 sm:p-4 text-center">
                <div className="text-base sm:text-2xl md:text-3xl font-black text-white leading-tight">{formatCurrency(stats.total_spend)}</div>
                <div className="text-xs sm:text-sm text-white/70">Total Claimed</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-3 sm:p-4 text-center">
                <div className="text-lg sm:text-2xl md:text-3xl font-black text-white">{formatCurrency(stats.average_spend)}</div>
                <div className="text-xs sm:text-sm text-white/70">Average per MP</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-3 sm:p-4 text-center">
                <div className="text-2xl sm:text-3xl font-black text-amber-300">{flaggedCount}</div>
                <div className="text-xs sm:text-sm text-white/70">Flagged MPs</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Year Tabs */}
        <div className="flex gap-2 mb-6">
          <YearTab 
            year="2024-25" 
            isActive={selectedYear === '2024-25'} 
            onClick={() => setSelectedYear('2024-25')}
            hasData={true}
          />
          <YearTab 
            year="2025-26" 
            isActive={selectedYear === '2025-26'} 
            onClick={() => setSelectedYear('2025-26')}
            hasData={false}
          />
        </div>

        {selectedYear === '2025-26' ? (
          <BudgetDisplay2526 />
        ) : (
          <>
            {/* Info Box */}
            <div className="bg-amber-500/20 border border-amber-500/30 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 flex items-start gap-2 sm:gap-3">
              <Info className="h-4 w-4 sm:h-5 sm:w-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-xs sm:text-sm text-slate-300">
                <strong className="text-amber-300">About this data:</strong> MPs claim expenses for office costs, 
                staffing, accommodation, and travel. This is separate from their £93,904 salary.
                <a 
                  href="https://www.theipsa.org.uk/mp-staffing-business-costs" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="ml-1 sm:ml-2 text-amber-400 hover:text-amber-300 inline-flex items-center gap-1"
                >
                  Source <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>

            {/* Anomaly Alert Box */}
            <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
              <div className="flex items-start gap-2 sm:gap-3">
                <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs sm:text-sm text-slate-300 flex-1">
                  <strong className="text-red-300">Expense Flags:</strong> We flag MPs with unusual expense patterns. 
                  <strong className="text-red-300"> {flaggedCount} MPs</strong> currently flagged.
                </div>
              </div>
              <div className="mt-2 ml-6 sm:ml-8">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowOnlyFlagged(!showOnlyFlagged)}
                  className={`text-xs ${showOnlyFlagged ? 'bg-red-600 text-white border-red-600' : 'border-red-500/50 text-red-300 hover:bg-red-500/20'}`}
                >
                  <Eye className="h-3 w-3 mr-1" />
                  {showOnlyFlagged ? 'Show All' : 'Flagged Only'}
                </Button>
              </div>
            </div>

            {/* Top 10 Leaderboard */}
            {!searchQuery && !showOnlyFlagged && (
              <div className="mb-6 sm:mb-8">
                <h2 className="text-lg sm:text-2xl font-black text-white mb-3 sm:mb-4 flex items-center gap-2">
                  <Trophy className="h-5 w-5 sm:h-6 sm:w-6 text-amber-400" />
                  Top 10 Biggest Spenders
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
                  {top10.map((mp, index) => {
                    const slug = mp.name?.toLowerCase().replace(/ /g, '-').replace(/'/g, '') || '';
                    const anomalies = detectAnomalies(mp, averageSpend);
                    return (
                      <Link
                        key={mp.name}
                        to={`/politician/${slug}`}
                        className={`block cursor-pointer rounded-xl p-2.5 sm:p-4 transition-all hover:scale-105 relative ${
                          index === 0 ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-slate-900' :
                          index === 1 ? 'bg-gradient-to-br from-slate-300 to-slate-400 text-slate-900' :
                          index === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-700 text-white' :
                          'bg-white/10 text-white hover:bg-white/20'
                        }`}
                        data-testid={`top10-card-${index + 1}`}
                      >
                        {anomalies.length > 0 && (
                          <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2">
                            <div className={`${anomalies.find(a => a.severity === 'alert') ? 'bg-red-500' : 'bg-amber-500'} text-white text-[10px] px-1 py-0.5 rounded-full flex items-center`}>
                              <AlertTriangle className="h-2.5 w-2.5" />
                            </div>
                          </div>
                        )}
                        <div className={`text-xl sm:text-3xl font-black mb-0.5 sm:mb-1 ${index < 3 ? '' : 'text-emerald-400'}`}>
                          #{index + 1}
                        </div>
                        <div className="font-bold text-xs sm:text-sm truncate">{mp.name}</div>
                        <div className={`text-[10px] sm:text-xs truncate mb-1 sm:mb-2 ${index < 3 ? 'opacity-70' : 'text-slate-400'}`}>
                          {mp.constituency || 'N/A'}
                        </div>
                        <div className="font-black text-sm sm:text-lg">{formatCurrency(mp.total_spend)}</div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Controls */}
            <div className="flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                <Input
                  placeholder="Search MP by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 sm:pl-10 bg-white/10 border-slate-700 text-white placeholder:text-slate-500 text-sm"
                  data-testid="expenses-search-input"
                />
              </div>
              
              <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1">
                {sortOptions.map(option => (
                  <Button
                    key={option.value}
                    variant={sortBy === option.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSortBy(option.value)}
                    className={`flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3 ${sortBy === option.value 
                      ? "bg-emerald-600 hover:bg-emerald-700" 
                      : "border-slate-600 text-slate-300 hover:bg-slate-800"
                    }`}
                  >
                    {option.icon}
                    <span className="ml-1 hidden sm:inline">{option.label}</span>
                  </Button>
                ))}
              </div>
            </div>

            {/* Main List */}
            <div className="space-y-2 sm:space-y-3">
              <h2 className="text-base sm:text-xl font-bold text-white flex items-center gap-2">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-400" />
                {searchQuery 
                  ? `Results (${displayLeaderboard.length})`
                  : showOnlyFlagged 
                    ? `Flagged (${displayLeaderboard.length})`
                    : `All MPs (${remainingMPs.length})`
                }
              </h2>
              
              {loading ? (
                <div className="space-y-3">
                  {[...Array(10)].map((_, i) => (
                    <div key={i} className="bg-white/5 rounded-xl h-24 animate-pulse" />
                  ))}
                </div>
              ) : remainingMPs.length === 0 ? (
                <div className="bg-white/5 rounded-xl p-8 text-center">
                  <Search className="h-12 w-12 mx-auto mb-4 text-slate-500" />
                  <p className="text-slate-400">
                    {searchQuery 
                      ? `No MPs found matching "${searchQuery}"`
                      : showOnlyFlagged 
                        ? 'No flagged MPs found'
                        : 'No more MPs to display'
                    }
                  </p>
                  <p className="text-sm text-slate-500 mt-2">Try adjusting your search or filters</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {remainingMPs.slice(0, 100).map((mp) => {
                    const actualRank = leaderboard.findIndex(m => m.name === mp.name) + 1;
                    return (
                      <LeaderboardCard 
                        key={mp.parliament_id || mp.name} 
                        mp={mp} 
                        rank={actualRank}
                        averageSpend={averageSpend}
                      />
                    );
                  })}
                </div>
              )}
              
              {remainingMPs.length > 100 && (
                <div className="text-center py-4">
                  <p className="text-slate-400 text-sm">
                    Showing 100 of {remainingMPs.length} MPs. Use search to find specific MPs.
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MPExpensesPage;
